
import React, { useEffect, useRef, useState } from 'react';
import { Assessment, Category } from '@/types/assessment';
import { Card, CardFooter } from '@/components/ui/card';
import { useRecommendations } from '@/hooks/useRecommendations';
import { calculateOverallMaturity } from '@/utils/maturityLevelUtils';
import AssessmentCompleteBanner from './results/AssessmentCompleteBanner';
import AssessmentCompleteActions from './results/AssessmentCompleteActions';
import AssessmentCompleteContent from './results/AssessmentCompleteContent';
import { ResponseData } from '@/hooks/useAssessmentResponses';
import { supabase } from '@/integrations/supabase/client';
import { generateStandalonePDF } from '@/utils/pdfGenerator';
import { toast } from 'sonner';

interface AssessmentCompleteProps {
  assessment: Assessment;
  categories: Category[];
  scoresByCategory: Record<string, {current: number, target: number}>;
  responses?: Record<string, ResponseData>;
  onRestart: () => void;
  onExit: () => void;
}

const AssessmentComplete = ({ 
  assessment, 
  categories, 
  scoresByCategory, 
  responses,
  onRestart, 
  onExit 
}: AssessmentCompleteProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const emailSendingRef = useRef(false);
  
  // Generate recommendations based on individual responses only
  const { collectRecommendationsFromAnswers } = useRecommendations({
    assessment,
    categories,
    responses
  });
  
  const recommendations = collectRecommendationsFromAnswers();
  
  // Calculate overall maturity scores
  const maturityResults = calculateOverallMaturity(scoresByCategory);

  // Send email with styled PDF after page renders
  useEffect(() => {
    const sendEmailWithStyledPDF = async () => {
      console.log('[EMAIL DEBUG] Starting email send process', {
        emailSent,
        emailSendingRef: emailSendingRef.current,
        assessmentId: assessment.id
      });

      if (emailSent || emailSendingRef.current) {
        return;
      }
      emailSendingRef.current = true;

      // Get personal info from saved progress
      const progressRaw = localStorage.getItem(`assessment-progress-${assessment.id}`);
      
      let email = '';
      let fullName = '';
      let company = '';
      let designation = '';
      
      if (progressRaw) {
        try {
          const progress = JSON.parse(progressRaw);
          email = progress.personalInfo?.email || '';
          fullName = progress.personalInfo?.fullName || '';
          company = progress.personalInfo?.company || '';
          designation = progress.personalInfo?.designation || '';
        } catch (e) {
          toast.error('Failed to read your information');
          emailSendingRef.current = false;
          return;
        }
      }
      
      if (!email) {
        toast.warning('We could not send the results because your email is missing. Please fill Personal Info and try again.');
        emailSendingRef.current = false;
        return;
      }

      try {
        await new Promise(resolve => setTimeout(resolve, 3000));

        if (!contentRef.current) {
          toast.error('Failed to prepare assessment results');
          emailSendingRef.current = false;
          return;
        }

        const pdfBlob = await generateStandalonePDF(
          assessment,
          categories,
          scoresByCategory,
          maturityResults.current,
          maturityResults.target,
          recommendations
        );
        
        if (!pdfBlob) {
          toast.error('Failed to generate PDF');
          emailSendingRef.current = false;
          return;
        }

        // Convert blob to base64 and upload to Google Drive
        const reader = new FileReader();
        reader.readAsDataURL(pdfBlob);
        
        await new Promise<void>((resolve, reject) => {
          reader.onloadend = async () => {
            try {
              const base64data = reader.result as string;
              const base64Content = base64data.split(',')[1];
              
              toast.info('Preparing your assessment results...');
              
              const { data: uploadData, error: uploadError } = await supabase.functions.invoke('upload-to-google-drive', {
                body: {
                  fileData: base64Content,
                  fileName: `${assessment.title} - Results - ${Date.now()}.pdf`,
                }
              });

              if (uploadError) {
                toast.error(`Failed to prepare your results: ${uploadError.message || 'Unknown error'}`);
                emailSendingRef.current = false;
                reject(uploadError);
                return;
              }

              const googleDriveLink = uploadData?.publicUrl || uploadData?.mediaLink;
              setPdfUrl(googleDriveLink);
              
              // Send email with Google Drive link
              toast.info(`Sending your results to ${email}...`);
              
              const { data: emailData, error: emailError } = await supabase.functions.invoke('send-assessment-confirmation', {
                body: {
                  email,
                  fullName: fullName || 'there',
                  assessmentTitle: assessment.title,
                  currentMaturity: maturityResults?.current?.label,
                  targetMaturity: maturityResults?.target?.label,
                  googleDriveLink: googleDriveLink,
                }
              });

              if (emailError) {
                toast.error(`Failed to send confirmation email: ${emailError.message || 'Unknown error'}`);
                emailSendingRef.current = false;
                reject(emailError);
              } else {
                toast.success('We\'ve emailed your results with a link to download the PDF.');
                setEmailSent(true);
                resolve();
              }
            } catch (error: any) {
              toast.error(`Failed to process results: ${error.message || 'Unknown error'}`);
              emailSendingRef.current = false;
              reject(error);
            }
          };
          
          reader.onerror = (error) => {
            toast.error('Failed to read PDF file');
            emailSendingRef.current = false;
            reject(error);
          };
        });
      } catch (error: any) {
        toast.error(`Failed to send results: ${error.message || 'Unknown error'}`);
        emailSendingRef.current = false;
      }
    };

    sendEmailWithStyledPDF();
  }, [assessment.title, emailSent]);

  // Handle restarting the assessment - clear saved results and call parent handler
  const handleRestart = () => {
    localStorage.removeItem('assessment-results-scores');
    onRestart(); // Parent will clear responses and progress
  };

  // Get personal info for passing to actions
  const getPersonalInfo = () => {
    const progressRaw = localStorage.getItem(`assessment-progress-${assessment.id}`);
    if (progressRaw) {
      try {
        const progress = JSON.parse(progressRaw);
        return {
          fullName: progress.personalInfo?.fullName || '',
          email: progress.personalInfo?.email || '',
          company: progress.personalInfo?.company || '',
          designation: progress.personalInfo?.designation || ''
        };
      } catch (e) {
        // Failed to parse
      }
    }
    return undefined;
  };

  return (
    <div className="max-w-4xl mx-auto py-6">
      <Card className="border-none shadow-lg" ref={contentRef}>
        <AssessmentCompleteBanner title={assessment.title} />
        
        <AssessmentCompleteContent 
          assessment={assessment}
          categories={categories}
          scoresByCategory={scoresByCategory}
          maturityResults={maturityResults}
          recommendations={recommendations}
        />
        
        <CardFooter className="flex flex-col sm:flex-row justify-between gap-4">
          <AssessmentCompleteActions 
            calendlyUrl={assessment.calendlyLink}
            onRestart={handleRestart}
            onExit={onExit}
            onDownloadReport={async () => {}} // Will be handled by content component
            personalInfo={getPersonalInfo()}
            assessmentTitle={assessment.title}
            pdfUrl={pdfUrl}
          />
        </CardFooter>
      </Card>
    </div>
  );
};

export default AssessmentComplete;
